import React, { useState } from "react";
import { Form, Button } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";
import { API, API_KEY_HEADERS } from "../config/apiConfig";
import SuccessModal from "../components/SuccessModal";

// Validation schema using Yup
const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  subject: Yup.string()
    .required("Subject is required"),
  message: Yup.string()
    .required("Message is required"),
  subscribe: Yup.boolean(),
});

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsSubmitting(true);

    try {
   

      // Make API call to backend
      const response = await axios.post(API.submitContact, values, {
        headers: API_KEY_HEADERS,
      });

      if (response.status === 201) {

        // Show success modal
        // setShowSuccessModal(true);

        // Reset form
        resetForm();

        // Show success toast
        toast.success(
          "Message sent successfully! We'll get back to you soon.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      } else {
        toast.error("Unexpected response from server. Please try again.", {
          position: "top-right",
          autoClose: 5000,
        });
        console.error("Unexpected response status:", response.status);
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      console.error("Full error object:", error);

      let errorMessage = "Failed to send message. Please try again.";

      if (error.response) {
        // console.error("Error response status:", error.response.status);

        // Handle specific error cases
        if (error.response.status === 401) {
          errorMessage = "Authentication failed. Please check your API key.";
        } else if (error.response.status === 403) {
          errorMessage = "Access denied. Please check your permissions.";
        } else if (error.response.status === 429) {
          errorMessage = "Too many requests. Please try again later.";
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        console.error("No response received:", error.request);
        errorMessage = "No response from server. Please check your connection.";
      } else {
        console.error("Error setting up request:", error.message);
        errorMessage = `Request failed: ${error.message}`;
      }

      // Show error toast
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 7000,
      });
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      <div id="contact" className="contact-page py-5">
        <div className="container">
          {/* Page Header */}
          <div className="text-center mb-5">
            <h2 className="section-title">Contact Us</h2>
            <p className="lead">
              We'd love to hear from you. Get in touch with our team.
            </p>
          </div>

          <div className="row">
            {/* Contact Information */}
            <div className="col-lg-5 mb-5 mb-lg-0">
              <div className="card h-100 shadow-sm">
                <div className="card-body p-4">
                  <h3 className="mb-4">Contact Information</h3>

                  <div className="d-flex mb-4">
                    <div
                      className="icon-box bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div>
                      <h5 className="mb-1">Our Location</h5>
                      <p className="mb-0">New Zealand</p>
                    </div>
                  </div>

                  <div className="d-flex mb-4">
                    <div
                      className="icon-box bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div>
                      <h5 className="mb-1">Email Us</h5>
                      <p className="mb-0">
                        <a href="mailto:info@asiaacademicpress.com">
                          info@asiaacademicpress.com
                        </a>
                        <br />
                      </p>
                    </div>
                  </div>

                  <hr className="my-4" />
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-7">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h3 className="mb-4">Send Us a Message</h3>

                  <Formik
                    initialValues={{
                      name: "",
                      email: "",
                      subject: "",
                      message: "",
                      subscribe: false,
                    }}
                    validationSchema={ContactSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <Form.Group>
                              <Form.Label htmlFor="name">
                                Your Name <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="name"
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.name && errors.name}
                                placeholder="Enter your full name"
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                          <div className="col-md-6 mb-3">
                            <Form.Group>
                              <Form.Label htmlFor="email">
                                Email Address{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="email"
                                id="email"
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.email && errors.email}
                                placeholder="Enter your email address"
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.email}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </div>
                        </div>

                        <div className="mb-3">
                          <Form.Group>
                            <Form.Label htmlFor="subject">
                              Subject <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              id="subject"
                              name="subject"
                              value={values.subject}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.subject && errors.subject}
                              placeholder="Enter message subject"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.subject}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="mb-3">
                          <Form.Group>
                            <Form.Label htmlFor="message">
                              Your Message{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              id="message"
                              name="message"
                              rows="5"
                              value={values.message}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.message && errors.message}
                              placeholder="Enter your message here..."
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.message}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              {values.message.length}/1000 characters
                            </Form.Text>
                          </Form.Group>
                        </div>

                        <div className="mb-4">
                          <Form.Group>
                            <Form.Check
                              type="checkbox"
                              id="subscribe"
                              name="subscribe"
                              checked={values.subscribe}
                              onChange={handleChange}
                              label="Subscribe to our newsletter for the latest updates and announcements"
                            />
                          </Form.Group>
                        </div>

                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          className="px-4"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Sending...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane me-2"></i>
                              Send Message
                            </>
                          )}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={closeSuccessModal}
        title="Message Sent Successfully!"
        message="Thank you for contacting us! We have received your message and will get back to you within 24-48 hours. You'll also receive a confirmation email."
      />
    </>
  );
};

export default Contact;
